// Lightweight Shaka Player initializer used across the app.
// Prioritizes Cloudflare Stream HLS (adaptive) and falls back to MP4.

// whatwg-fetch removed - native fetch supported
import shaka from 'shaka-player/dist/shaka-player.compiled.js';

// Install polyfills once
if (shaka && shaka.polyfill && typeof shaka.polyfill.installAll === 'function') {
  try { shaka.polyfill.installAll(); } catch (_) {}
}

export function isShakaSupported() {
  return shaka.Player.isBrowserSupported();
}

/**
 * Initialize Shaka on a given <video> element.
 * - Loads HLS/DASH manifest first (preferred for quality and ABR)
 * - Falls back to MP4 if manifest fails or Shaka unsupported
 *
 * @param {HTMLVideoElement} videoEl
 * @param {string} manifestUrl - HLS (.m3u8) or DASH (.mpd)
 * @param {string|undefined} mp4FallbackUrl
 * @param {Object} options
 * @param {boolean} [options.autoplay=true]
 * @param {string|undefined} [options.poster]
 * @returns {Promise<shaka.Player|null>}
 */
export async function initShakaOn(videoEl, manifestUrl, mp4FallbackUrl, options = {}) {
  const { autoplay = true, poster } = options;

  if (!videoEl) return null;

  if (!isShakaSupported()) {
    // Graceful fallback to plain MP4
    if (mp4FallbackUrl) {
      videoEl.setAttribute('src', mp4FallbackUrl);
      videoEl.setAttribute('type', 'video/mp4');
      try { console.info('[Shaka] Browser not supported. Using MP4 fallback:', mp4FallbackUrl); } catch (_) {}
    }
    return null;
  }

  // Use attach() pattern (constructor without mediaElement) per deprecation notice
  const player = new shaka.Player();

  // Favor higher quality on startup; ABR remains enabled for stability.
  player.configure({
    abr: {
      enabled: true,
      // Start with a high default bandwidth estimate (25 Mbps) so we begin near top rendition
      defaultBandwidthEstimate: 25_000_000
    },
    streaming: {
      bufferingGoal: 30,  // seconds
      rebufferingGoal: 2,
      lowLatencyMode: false
    },
    // Let the browser handle FairPlay on Safari when needed
    drm: {},
    manifest: {
      // Keep defaults; HLS/DASH auto-detected
    }
  });

  if (poster) {
    videoEl.setAttribute('poster', poster);
  }

  try {
    // If there are native <track> elements (for browser fallback), remove them
    // so they don't appear in Shaka UI alongside side-loaded tracks.
    // We only do this in the Shaka-supported path; fallback path keeps them.
    try {
      const nativeTracks = Array.from(videoEl.querySelectorAll('track'));
      nativeTracks.forEach((t) => t.remove());
      if (videoEl.textTracks && videoEl.textTracks.length) {
        for (let i = 0; i < videoEl.textTracks.length; i += 1) {
          const tt = videoEl.textTracks[i];
          if (tt) tt.mode = 'disabled';
        }
      }
    } catch (_) {}

    try { console.info('[Shaka] Attempting HLS manifest:', manifestUrl); } catch (_) {}
    // Attach to media element before loading
    await player.attach(videoEl);
    await player.load(manifestUrl);
    try { console.info('[Shaka] Streaming via HLS manifest:', manifestUrl); } catch (_) {}

    // Side-load WebVTT captions if provided via data attributes
    const vttUrl = videoEl.dataset.vttUrl;
    const vttLang = videoEl.dataset.vttLang || 'en';
    const vttLabel = videoEl.dataset.vttLabel || 'English';
    if (vttUrl) {
      try {
        await player.addTextTrackAsync(vttUrl, vttLang, 'subtitles', 'text/vtt', vttLabel);
        // Keep captions hidden by default; user can enable via controls
        player.setTextTrackVisibility(false);
      } catch (e) {
        // If side-loading fails, continue without captions
      }
    }

    if (autoplay) {
      // Try to autoplay unmuted; if blocked, retry muted
      try {
        videoEl.muted = false;
        await videoEl.play();
      } catch (e1) {
        try {
          videoEl.muted = true;
          await videoEl.play();
        } catch (e2) {
          // Autoplay blocked entirely; rely on user gesture
        }
      }
    }

    return player;
  } catch (err) {
    // Manifest failed; fallback to MP4 if available
    if (mp4FallbackUrl) {
      videoEl.setAttribute('src', mp4FallbackUrl);
      videoEl.setAttribute('type', 'video/mp4');
      try { console.warn('[Shaka] Failed to load HLS. Falling back to MP4:', { manifestUrl, mp4FallbackUrl, error: err && err.message }); } catch (_) {}
    }
    return null;
  }
}

/**
 * Auto-initialize all videos that declare data attributes.
 * Looks for: data-hls-url (or data-dash-url), optional data-mp4-url, data-poster
 * 
 * Skips lazy videos to avoid loading hidden carousel slides:
 * - Videos with preload="none" 
 * - Videos with data-lazy-shaka="true"
 * These will be initialized when they become visible (e.g., carousel slide change)
 */
export function autoInitShaka() {
  const selector = 'video[data-hls-url], video[data-dash-url]';
  document.querySelectorAll(selector).forEach(async (el) => {
    if (el.dataset.shakaInitialized === 'true') return;
    
    // Skip lazy videos - they'll be initialized when visible
    if (el.preload === 'none' || el.dataset.lazyShaka === 'true') return;
    
    const manifestUrl = el.dataset.hlsUrl || el.dataset.dashUrl;
    const mp4Url = el.dataset.mp4Url;
    const poster = el.dataset.poster;
    if (!manifestUrl) return;

    el.dataset.shakaInitialized = 'true';
    try { console.info('[Shaka] Auto-init video element:', { id: el.id, manifestUrl, mp4Url }); } catch (_) {}
    await initShakaOn(el, manifestUrl, mp4Url, { autoplay: el.autoplay, poster });
  });
}

/**
 * Initialize a specific video element with Shaka
 * Used for lazy loading videos when they become visible
 */
export function initShakaElement(el) {
  if (!el || el.dataset.shakaInitialized === 'true') return;
  
  const manifestUrl = el.dataset.hlsUrl || el.dataset.dashUrl;
  const mp4Url = el.dataset.mp4Url;
  const poster = el.dataset.poster;
  if (!manifestUrl) return;

  el.dataset.shakaInitialized = 'true';
  return initShakaOn(el, manifestUrl, mp4Url, { autoplay: el.autoplay, poster });
}


